import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { PaperPlaneTilt, ArrowLeft, Plus, ChatCircleDots, Trash, ChatsCircle, FilePpt, DownloadSimple, Warning, UploadSimple, CircleNotch, Sparkle } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import MarkdownMessage from "@/components/MarkdownMessage";
import NicheDocStrip from "@/components/NicheDocStrip";
import BlueprintDownloadButton, { isFullBlueprint, isAssembledBook, isCredibilityCode, isDocumentReply } from "@/components/BlueprintDownloadButton";
import { quickReplyFor, PRACTICE_START } from "@/components/PracticeBlocks";
import { useTheme } from "@/contexts/ThemeContext";

const NICHE_DOC_GPT = "Editorial Website Builder";
const LINKEDIN_BLUEPRINT_GPT = "LinkedIn & Social Media Blueprint Builder";
const STRATEGY_THINKING_GPT = "Strategy Thinking Framework";
const BOOK_BUILDER_GPT = "Book Builder";
const CREDIBILITY_CODE_GPT = "Credibility Code";
const DOCUMENT_BUILDER_GPT = "Document Builder";
const NICHE_START_GPTS = [LINKEDIN_BLUEPRINT_GPT, STRATEGY_THINKING_GPT, CREDIBILITY_CODE_GPT, DOCUMENT_BUILDER_GPT];
const CREDIBILITY_PASTE_PREFIX = "Here is my Credibility Code:";
const NICHE_DOC_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];

export default function Chat() {
  const { gptId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [gpt, setGpt] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState("");
  const [nicheDoc, setNicheDoc] = useState(null);
  const [nicheUploading, setNicheUploading] = useState(false);
  const [nicheError, setNicheError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const nicheFileRef = useRef(null);
  const usesNicheDoc = gpt?.name === NICHE_DOC_GPT;
  const usesNicheBlueprint = gpt?.name === LINKEDIN_BLUEPRINT_GPT;
  const usesNicheStart = NICHE_START_GPTS.includes(gpt?.name);
  const isStrategyCase = gpt?.name === STRATEGY_THINKING_GPT;
  const isBookBuilder = gpt?.name === BOOK_BUILDER_GPT;
  const [latestBook, setLatestBook] = useState(null);
  useEffect(() => {
    if (!isBookBuilder) return;
    api.get("/books/latest").then(({ data }) => setLatestBook(data)).catch(() => setLatestBook(null));
  }, [isBookBuilder, messages.length]);
  const isCredibility = gpt?.name === CREDIBILITY_CODE_GPT;
  const isDocumentBuilder = gpt?.name === DOCUMENT_BUILDER_GPT;
  const [latestCode, setLatestCode] = useState(null);
  useEffect(() => {
    if (!isCredibility && !isDocumentBuilder) return;
    api.get("/credibility-codes/latest").then(({ data }) => setLatestCode(data)).catch(() => setLatestCode(null));
  }, [isCredibility, isDocumentBuilder, messages.length]);
  // Document Builder: offer the finished Credibility Code only while the GPT is asking for it (latest reply mentions it), until sent.
  const codeAlreadyShared = messages.some((m) => m.role === "user" && m.content.trimStart().startsWith(CREDIBILITY_PASTE_PREFIX));
  const lastMsg = messages[messages.length - 1];
  const gptAsksForCode = lastMsg?.role === "assistant" && /credibility code/i.test(lastMsg.content);
  const showUseCode = isDocumentBuilder && latestCode?.document && activeId && gptAsksForCode && !codeAlreadyShared;
  const [blueprintBusy, setBlueprintBusy] = useState(false);
  const [latestBlueprint, setLatestBlueprint] = useState(null);

  // GPT home: offer the most recent full blueprint from any non-deleted chat in this GPT.
  useEffect(() => {
    if (!usesNicheBlueprint) return;
    api.get("/blueprints/latest").then(({ data }) => setLatestBlueprint(data)).catch(() => setLatestBlueprint(null));
  }, [usesNicheBlueprint, messages.length]);

  // Starter for the LinkedIn Blueprint GPT: fetch the participant's FINALIZED niche from Define Your Niche and
  // build straight away; if none exists, send the normal starter so the GPT asks for a paste.
  const startBlueprint = async () => {
    if (sending || !activeId || blueprintBusy) return;
    setBlueprintBusy(true);
    let text = gpt.conversation_starter;
    try {
      const [{ data }] = await Promise.all([api.get("/niche-blueprint/latest"), new Promise((r) => setTimeout(r, 900))]);
      text = `Here is my finalized Niche Blueprint from Define Your Niche:\n\n${data.content}`;
    } catch {
      /* no finalized niche → plain starter, GPT asks for a paste */
    } finally {
      setBlueprintBusy(false);
    }
    await send(null, text);
  };

  useEffect(() => {
    if (!usesNicheDoc) return;
    api.get(`/niche-doc`).then(({ data }) => setNicheDoc(data.doc)).catch(() => {});
  }, [usesNicheDoc]);

  const uploadNicheDoc = async (file) => {
    if (!file || nicheUploading) return;
    setNicheError("");
    if (!NICHE_DOC_TYPES.includes(file.type)) {
      setNicheError("Please upload a PDF, PNG or JPG file.");
      return;
    }
    if (file.size > 32 * 1024 * 1024) {
      setNicheError("That file is over 32 MB. Please upload a smaller copy.");
      return;
    }
    setNicheUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/niche-doc/upload`, form, { timeout: 180000 });
      setNicheDoc(data.doc);
    } catch (err) {
      setNicheError(err.response?.data?.detail || "We couldn't read the document just now. Please try again.");
    } finally {
      setNicheUploading(false);
    }
  };

  const onDrop = (e) => {
    if (!usesNicheDoc) return;
    e.preventDefault();
    setDragOver(false);
    uploadNicheDoc(e.dataTransfer.files?.[0]);
  };

  // While a reply is pending, poll the LLM pool so queued users see why it's slow
  useEffect(() => {
    if (!sending) {
      setQueue(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/chat/pool-status`);
        if (!cancelled) setQueue(data);
      } catch {
        // ignore
      }
    };
    const timer = setTimeout(poll, 1500);
    const interval = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [sending]);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: gptData }, { data: convs }] = await Promise.all([
          api.get(`/gpts/${gptId}`),
          api.get(`/conversations`, { params: { gpt_id: gptId } }),
        ]);
        setGpt(gptData);
        setConversations(convs);
        if (convs.length > 0) {
          setActiveId(convs[0].id);
        } else {
          const { data: c } = await api.post(`/conversations`, { gpt_id: gptId });
          setConversations([c]);
          setActiveId(c.id);
        }
      } catch (e) {
        if (e.response?.status === 403) setError("This GPT is locked.");
        else if (e.response?.status === 404) setError("GPT not found.");
        else setError("Failed to load chat.");
      }
    })();
  }, [gptId]);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/conversations/${activeId}/messages`);
        setMessages(data);
      } catch {
        setMessages([]);
      }
    })();
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Poll for presentation updates while a pending-presentation message exists
  useEffect(() => {
    if (!activeId) return;
    const hasPending = messages.some(
      (m) => m.metadata && m.metadata.kind === "presentation_pending"
    );
    if (!hasPending) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/conversations/${activeId}/messages`);
        setMessages(data);
      } catch {
        // swallow; will retry
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [messages, activeId]);

  const newConversation = async () => {
    try {
      const { data } = await api.post(`/conversations`, { gpt_id: gptId });
      setConversations([data, ...conversations]);
      setActiveId(data.id);
      setMessages([]);
    } catch {
      setError("Could not create conversation.");
    }
  };

  const deleteConversation = async (id) => {
    try {
      await api.delete(`/conversations/${id}`);
      const remaining = conversations.filter((c) => c.id !== id);
      setConversations(remaining);
      if (activeId === id) {
        if (remaining.length > 0) setActiveId(remaining[0].id);
        else newConversation();
      }
    } catch {
      // ignore
    }
  };

  const [downloadingKey, setDownloadingKey] = useState(null);

  const downloadPresentation = async (msg, format = "pptx") => {
    const key = `${msg.id}:${format}`;
    if (downloadingKey === key) return;
    setDownloadingKey(key);

    const title = (msg.metadata && msg.metadata.title) || "presentation";
    const safeName =
      title.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^[._]+|[._]+$/g, "") ||
      "presentation";
    const filename = `${safeName}.${format}`;

    try {
      // 1) Ask backend for a signed, short-lived download URL
      const { data } = await api.post(
        `/presentations/${msg.id}/download-link`,
        null,
        { params: { format } }
      );
      const rel = data.relative_url || data.url;
      let fullUrl;
      if (/^https?:\/\//i.test(rel)) {
        fullUrl = rel;
      } else {
        const origin = API.replace(/\/api\/?$/, "");
        fullUrl = `${origin}${rel.startsWith("/") ? rel : `/${rel}`}`;
      }

      // 2) Fetch the file as a blob and save it. This is the one approach
      //    that works identically across ALL browsers for ALL file types
      //    (PPTX, PDF, DOCX…) because the browser has no chance to "open"
      //    a blob — a blob URL with <a download> always triggers a save.
      //    It also bypasses Chrome's "open PDFs in browser" preference that
      //    otherwise causes a PDF <a> click to open a viewer tab instead of
      //    downloading.
      const resp = await fetch(fullUrl, {
        method: "GET",
        cache: "no-store",
        credentials: "omit",
      });
      if (!resp.ok) {
        throw new Error(`Download failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Delay revoke so Safari / mobile browsers have time to commit the save
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(blobUrl);
          a.remove();
        } catch {
          // ignore
        }
      }, 8000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : `Could not download the ${format.toUpperCase()}. Please try again.`
      );
    } finally {
      setTimeout(() => setDownloadingKey(null), 800);
    }
  };

  const send = async (e, overrideText) => {
    if (e && e.preventDefault) e.preventDefault();
    const text = (overrideText !== undefined ? overrideText : input).trim();
    if (!text || sending || !activeId) return;
    if (overrideText === undefined) setInput("");
    setSending(true);
    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, { content: text });
      setMessages((m) => {
        const without = m.filter((x) => x.id !== optimistic.id);
        const next = [...without, data.user_message, data.assistant_message];
        if (data.pending_message) next.push(data.pending_message);
        return next;
      });
      setConversations((cs) =>
        cs.map((c) =>
          c.id === activeId
            ? { ...c, title: c.title === "New conversation" ? text.slice(0, 60) : c.title }
            : c
        )
      );
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setError(err.response?.data?.detail || "We couldn't reach the hub just now. Please check your connection and resend your message (no need to refresh).");
    } finally {
      setSending(false);
    }
  };

  if (error && !gpt) {
    return (
      <div className="min-h-screen chat-shell">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <p className="text-red-500 mb-4" data-testid="chat-error">{error}</p>
          <Link to="/" className="pill-soft"><ArrowLeft size={16} /> Back to gallery</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen chat-shell flex flex-col" data-testid="chat-page">
      <Navbar />

      <div className="flex-1 max-w-7xl w-full mx-auto px-4 lg:px-8 py-6 grid lg:grid-cols-[300px_1fr] gap-5">
        {/* Sidebar - conversations */}
        <aside className="chat-history-card h-fit min-w-0 lg:sticky lg:top-24" data-testid="chat-sidebar">
          <div className="flex items-center justify-between mb-3 px-1">
            <p className="label-uppercase" style={{ color: "var(--muted)" }}>History</p>
            <button
              onClick={newConversation}
              className="pill-soft"
              style={{ height: "32px", padding: "0 0.7rem", fontSize: "0.78rem" }}
              data-testid="chat-new-conversation"
            >
              <Plus size={12} weight="bold" /> New
            </button>
          </div>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {conversations.map((c) => (
              <div
                key={c.id}
                onClick={() => setActiveId(c.id)}
                data-testid={`chat-conversation-${c.id}`}
                className={`chat-history-item group ${activeId === c.id ? "is-active" : ""}`}
              >
                <ChatCircleDots size={14} weight="duotone" />
                <span className="flex-1 truncate">{c.title}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(c.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`chat-delete-conv-${c.id}`}
                  title="Delete"
                  style={{ color: "currentColor" }}
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
            {conversations.length === 0 && (
              <p className="text-xs px-3 py-2" style={{ color: "var(--muted)" }}>No conversations yet.</p>
            )}
          </div>
        </aside>

        {/* Main chat */}
        <section className="chat-panel flex flex-col min-w-0" style={{ minHeight: "70vh" }}>
          {/* Header */}
          <div className="chat-header">
            <Link to="/" className="chat-header-back" data-testid="chat-back-button">
              <ArrowLeft size={16} weight="bold" />
            </Link>
            {gpt?.avatar_url ? (
              <img src={gpt.avatar_url} alt="" className="w-11 h-11 rounded-xl object-cover" />
            ) : (
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: "var(--lime)", color: "var(--ink)" }}
              >
                <ChatsCircle size={20} weight="duotone" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="label-uppercase" style={{ color: "var(--muted)", fontSize: "0.62rem" }}>{gpt?.category}</p>
              <h2 className="font-display text-xl font-bold truncate" data-testid="chat-gpt-name" style={{ marginTop: 2 }}>
                {gpt?.name}
              </h2>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className={`flex-1 overflow-y-auto p-5 sm:p-8 ${dragOver ? "niche-dropzone-active" : ""}`}
            data-testid="chat-messages-area"
            onDragOver={usesNicheDoc ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
            onDragLeave={usesNicheDoc ? () => setDragOver(false) : undefined}
            onDrop={usesNicheDoc ? onDrop : undefined}
          >
            <div className="max-w-3xl mx-auto space-y-4">
              {usesNicheDoc && nicheDoc && (
                <NicheDocStrip doc={nicheDoc} onReplace={uploadNicheDoc} busy={nicheUploading} />
              )}
              {usesNicheDoc && nicheDoc && nicheUploading && (
                <p className="niche-reading" data-testid="niche-doc-reading">
                  <span className="spinner" style={{ width: "0.9rem", height: "0.9rem" }} /> Reading your Niche Recommendation…
                </p>
              )}
              {usesNicheDoc && nicheDoc && nicheError && (
                <p className="error-banner" data-testid="niche-doc-error">{nicheError}</p>
              )}
              {messages.length === 0 && (
                <div className="chat-empty" data-testid="chat-empty-state">
                  <div className="chat-empty-icon"><ChatCircleDots size={26} weight="duotone" /></div>
                  <p className="font-display font-bold text-lg mb-1" style={{ color: "var(--foreground)" }}>
                    Start the consultation
                  </p>
                  <p className="text-sm">Ask {gpt?.name} anything related to {gpt?.category?.toLowerCase()}.</p>
                  {usesNicheDoc && !nicheDoc && (
                    <p className="text-sm mt-3" style={{ color: "var(--muted)" }} data-testid="niche-doc-hint">
                      Upload it once and The Editor will skip the questions it already answers.
                    </p>
                  )}
                  {usesNicheDoc && nicheUploading && !nicheDoc && (
                    <p className="niche-reading mt-5" data-testid="niche-doc-reading">
                      <span className="spinner" style={{ width: "0.9rem", height: "0.9rem" }} /> Reading your Niche Recommendation…
                    </p>
                  )}
                  {usesNicheDoc && nicheError && !nicheDoc && (
                    <p className="error-banner mt-4" data-testid="niche-doc-error">{nicheError}</p>
                  )}
                  <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                    {usesNicheDoc && !nicheDoc && !nicheUploading && (
                      <>
                        <button
                          type="button"
                          onClick={() => nicheFileRef.current?.click()}
                          className="pill-cta"
                          data-testid="niche-doc-upload-button"
                          disabled={sending}
                        >
                          Upload your Niche Recommendation
                          <span className="pill-cta-arrow"><UploadSimple size={12} weight="bold" /></span>
                        </button>
                        <input
                          ref={nicheFileRef}
                          type="file"
                          accept="application/pdf,image/png,image/jpeg"
                          className="hidden"
                          data-testid="niche-doc-file-input"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            uploadNicheDoc(f);
                          }}
                        />
                      </>
                    )}
                    {gpt?.conversation_starter && (
                      <button
                        type="button"
                        onClick={() => (usesNicheStart ? startBlueprint() : send(null, gpt.conversation_starter))}
                        className={`pill-cta${blueprintBusy ? " is-fetching" : ""}`}
                        data-testid="chat-conversation-starter"
                        disabled={sending || !activeId || nicheUploading || blueprintBusy}
                      >
                        {blueprintBusy ? "Fetching your finalized niche…" : gpt.conversation_starter}
                        <span className="pill-cta-arrow">{blueprintBusy ? <CircleNotch size={12} weight="bold" className="spin" /> : <PaperPlaneTilt size={12} weight="fill" />}</span>
                      </button>
                    )}
                    {isBookBuilder && latestBook && (
                      <div className="blueprint-home-download" data-testid="book-home-download">
                        <BlueprintDownloadButton url={`/conversations/${latestBook.conversation_id}/book.pdf`} label="Download my latest book (PDF)" testId="book-home-download-button" />
                        <span className="blueprint-built-label" data-testid="book-built-label">{latestBook.title} · {latestBook.chapters} chapters · assembled {latestBook.built_label.replace(" IST", "")}</span>
                      </div>
                    )}
                    {isCredibility && latestCode && (
                      <div className="blueprint-home-download" data-testid="credibility-home-download">
                        <BlueprintDownloadButton url={`/messages/${latestCode.message_id}/credibility-code.pdf`} label="Download my latest Credibility Code (PDF)" testId="credibility-home-download-button" />
                        <span className="blueprint-built-label" data-testid="credibility-built-label">{latestCode.name} · built {latestCode.built_label.replace(" IST", "")}</span>
                      </div>
                    )}
                    {usesNicheBlueprint && latestBlueprint && (
                      <div className="blueprint-home-download" data-testid="blueprint-home-download">
                        <BlueprintDownloadButton messageId={latestBlueprint.message_id} label="Download my latest Blueprint (PDF)" testId="blueprint-home-download-button" />
                        <span className="blueprint-built-label" data-testid="blueprint-built-label">Built {latestBlueprint.built_label.replace(" IST", "")}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {messages.map((m, idx) => {
                const kind = m.metadata && m.metadata.kind;
                if (m.role === "assistant" && kind === "presentation_superseded") {
                  return (
                    <div
                      key={m.id}
                      className="flex justify-start fade-up"
                      data-testid="chat-message-presentation-superseded"
                    >
                      <div className="presentation-card presentation-card--superseded">
                        <div className="presentation-card-header">
                          <div className="presentation-card-icon">
                            <FilePpt size={18} weight="duotone" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="label-uppercase" style={{ color: "var(--muted)", fontSize: "0.55rem" }}>
                              Earlier version
                            </p>
                            <p className="font-display font-semibold text-sm truncate" style={{ marginTop: 1 }}>
                              {m.metadata?.title || "Previous draft"}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {(m.metadata?.pptx_url || m.metadata?.download_url) && (
                            <button
                              type="button"
                              onClick={() => downloadPresentation(m, "pptx")}
                              className="pill-soft"
                              data-testid="chat-presentation-download-old-pptx-btn"
                              style={{ fontSize: "0.72rem", height: "30px" }}
                            >
                              <DownloadSimple size={12} weight="bold" />
                              PPTX
                            </button>
                          )}
                          {m.metadata?.pdf_url && (
                            <button
                              type="button"
                              onClick={() => downloadPresentation(m, "pdf")}
                              className="pill-soft"
                              data-testid="chat-presentation-download-old-pdf-btn"
                              style={{ fontSize: "0.72rem", height: "30px" }}
                            >
                              <DownloadSimple size={12} weight="bold" />
                              PDF
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                if (m.role === "assistant" && kind === "presentation_pending") {
                  return (
                    <div
                      key={m.id}
                      className="flex justify-start fade-up"
                      data-testid="chat-message-presentation-pending"
                    >
                      <div className="presentation-card presentation-card--pending">
                        <div className="presentation-card-header">
                          <div className="presentation-card-icon">
                            <FilePpt size={22} weight="duotone" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="label-uppercase" style={{ color: "var(--muted)", fontSize: "0.6rem" }}>
                              Crafting your deck
                            </p>
                            <p className="font-display font-bold text-base truncate" style={{ marginTop: 2 }}>
                              {m.metadata?.title || "Your presentation"}
                            </p>
                          </div>
                          <div className="spinner" style={{ width: "1.1rem", height: "1.1rem" }} />
                        </div>
                        <p className="text-sm mt-3" style={{ color: "var(--muted)" }}>
                          {m.content}
                        </p>
                      </div>
                    </div>
                  );
                }
                if (m.role === "assistant" && kind === "presentation_ready") {
                  return (
                    <div
                      key={m.id}
                      className="flex justify-start fade-up"
                      data-testid="chat-message-presentation-ready"
                    >
                      <div className="presentation-card presentation-card--ready">
                        <div className="presentation-card-header">
                          <div className="presentation-card-icon is-ready">
                            <FilePpt size={22} weight="duotone" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="label-uppercase" style={{ color: "var(--muted)", fontSize: "0.6rem" }}>
                              Presentation ready
                            </p>
                            <p className="font-display font-bold text-base truncate" style={{ marginTop: 2 }}>
                              {m.metadata?.title || "Your presentation"}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm mt-3 mb-4">{m.content}</p>
                        <div className="flex flex-wrap gap-2">
                          {(m.metadata?.pptx_url || m.metadata?.download_url) && (
                            <button
                              type="button"
                              onClick={() => downloadPresentation(m, "pptx")}
                              disabled={downloadingKey === `${m.id}:pptx`}
                              className="pill-cta"
                              data-testid="chat-presentation-download-pptx-btn"
                            >
                              <DownloadSimple size={14} weight="bold" />
                              {downloadingKey === `${m.id}:pptx` ? "Preparing…" : "Download PPTX"}
                            </button>
                          )}
                          {m.metadata?.pdf_url && (
                            <button
                              type="button"
                              onClick={() => downloadPresentation(m, "pdf")}
                              disabled={downloadingKey === `${m.id}:pdf`}
                              className="pill-soft"
                              data-testid="chat-presentation-download-pdf-btn"
                            >
                              <DownloadSimple size={14} weight="bold" />
                              {downloadingKey === `${m.id}:pdf` ? "Preparing…" : "Download PDF"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                if (m.role === "assistant" && kind === "presentation_failed") {
                  return (
                    <div
                      key={m.id}
                      className="flex justify-start fade-up"
                      data-testid="chat-message-presentation-failed"
                    >
                      <div className="presentation-card presentation-card--failed">
                        <div className="presentation-card-header">
                          <div className="presentation-card-icon is-failed">
                            <Warning size={22} weight="duotone" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="label-uppercase" style={{ color: "var(--muted)", fontSize: "0.6rem" }}>
                              Generation failed
                            </p>
                            <p className="font-display font-bold text-base">Please try again</p>
                          </div>
                        </div>
                        <p className="text-sm mt-3">{m.content}</p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={m.id}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} fade-up`}
                    data-testid={`chat-message-${m.role}`}
                  >
                    <div className={m.role === "user" ? "bubble-user" : "bubble-ai"}>
                      {m.role === "assistant" ? (
                        <>
                          <MarkdownMessage content={m.content} isDark={theme === "dark"} storageKey={m.id} />
                          {usesNicheBlueprint && isFullBlueprint(m.content) && (
                            <div className="blueprint-msg-download" data-testid={`blueprint-download-${m.id}`}>
                              <BlueprintDownloadButton messageId={m.id} testId={`blueprint-download-button-${m.id}`} />
                            </div>
                          )}
                          {isBookBuilder && isAssembledBook(m.content) && (
                            <div className="blueprint-msg-download" data-testid={`book-download-${m.id}`}>
                              <BlueprintDownloadButton url={`/conversations/${activeId}/book.pdf`} label="Download my book (PDF)" className="pill-cta" testId={`book-download-button-${m.id}`} />
                            </div>
                          )}
                          {isCredibility && isCredibilityCode(m.content) && (
                            <div className="blueprint-msg-download" data-testid={`credibility-download-${m.id}`}>
                              <BlueprintDownloadButton url={`/messages/${m.id}/credibility-code.pdf`} label="Download my Credibility Code (PDF)" className="pill-cta" testId={`credibility-download-button-${m.id}`} />
                            </div>
                          )}
                          {isDocumentBuilder && isDocumentReply(m.content) && (
                            <div className="blueprint-msg-download" data-testid={`document-download-${m.id}`}>
                              <BlueprintDownloadButton url={`/messages/${m.id}/document.pdf`} label="Download this document (PDF)" className="pill-cta" testId={`document-download-button-${m.id}`} />
                            </div>
                          )}
                          {isStrategyCase && idx === messages.length - 1 && !sending && quickReplyFor(m.content) && (
                            <div className="lets-solve-wrap" data-testid="lets-solve-wrap">
                              <button
                                type="button"
                                className="pill-cta"
                                data-testid={quickReplyFor(m.content) === "LET'S SOLVE" ? "lets-solve-button" : "practice-next-button"}
                                onClick={() => {
                                  const label = quickReplyFor(m.content);
                                  send(null, label === "Practice the client conversation" ? PRACTICE_START : label);
                                }}
                              >
                                {quickReplyFor(m.content)}
                                <span className="pill-cta-arrow"><PaperPlaneTilt size={12} weight="fill" /></span>
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="bubble-user-text">{m.content.replace(/\*\*(.*?)\*\*/g, "$1")}</div>
                      )}
                    </div>
                  </div>
                );
              })}
              {sending && (
                <div className="flex justify-start" data-testid="chat-typing-indicator">
                  <div className="chat-typing">
                    <div className="spinner" style={{ width: "1rem", height: "1rem" }} />
                    <span data-testid="chat-typing-text">
                      {queue && queue.waiting > 0
                        ? `High demand — you're in the queue (${queue.waiting} waiting, ${queue.active}/${queue.max} AI slots busy)…`
                        : "Thinking…"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Input */}
          {showUseCode && (
            <div className="use-code-row" data-testid="use-credibility-code-row">
              <button
                type="button"
                className="pill-cta pill-cta-outline pill-cta-sm"
                data-testid="use-credibility-code-button"
                disabled={sending}
                onClick={() => send(null, `${CREDIBILITY_PASTE_PREFIX}\n\n${latestCode.document}`)}
              >
                <Sparkle size={13} weight="fill" />
                Use my Credibility Code
                <span className="use-code-meta">{latestCode.name} · {latestCode.built_label.replace(" IST", "")}</span>
              </button>
            </div>
          )}
          <form onSubmit={send} className="chat-input-wrap" data-testid="chat-form">
            <div className="chat-input-row">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(e);
                  }
                }}
                placeholder={`Message ${gpt?.name || "the GPT"}...`}
                className="chat-input-field"
                rows={1}
                disabled={sending}
                data-testid="chat-input-field"
              />
              <button
                type="submit"
                className="chat-send-btn"
                disabled={sending || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send"
              >
                <PaperPlaneTilt size={18} weight="fill" />
              </button>
            </div>
            {error && (
              <p className="text-xs text-red-500 mt-2 max-w-3xl mx-auto" data-testid="chat-send-error">{error}</p>
            )}
          </form>
        </section>
      </div>
    </div>
  );
}
